import { useEffect, useState } from "react";
import { Loader, Truck, Mail, AlertTriangle, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { fmt } from "../lib/utils";
import Avatar from "./common/Avatar";

export default function SuppliersView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portfolio/suppliers")
      .then(r => setSuppliers(r.data.suppliers || []))
      .catch(() => setSuppliers([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-32"><Loader className="w-6 h-6 animate-spin text-faint" /></div>
  );

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <h1 className="text-greeting text-night">{t("nav.leverandorer")}</h1>
        <p className="text-[13.5px] text-muted mt-1">{t("suppliers.subtitle", { count: suppliers.length })}</p>
      </div>

      {suppliers.length === 0 ? (
        <div className="cx-card p-12 text-center">
          <Truck className="w-8 h-8 text-faint mx-auto mb-3" />
          <p className="text-[15px] font-bold text-night">{t("suppliers.empty")}</p>
          <p className="text-[13px] text-muted mt-1">{t("suppliers.empty_sub")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {suppliers.map((s: any, i: number) => {
            const pct = s.pct ?? null;  // F4: backend-eid %, vises verbatim (ikke re-beregn klientside)
            const over = (pct || 0) > 100;
            return (
            <button key={s.supplier_key || s.vendor_name} style={{ animationDelay: `${i * 35}ms` }}
              onClick={() => navigate(`/suppliers/${encodeURIComponent(s.supplier_key || s.vendor_name)}`)}
              className="cx-card p-5 animate-fade-up text-left transition-all hover:shadow-pop hover:-translate-y-0.5">
              <div className="flex items-center gap-3 mb-4">
                <Avatar name={s.vendor_name} size="md" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-[15px] font-bold text-night truncate">{s.vendor_name}</h3>
                  <p className="text-[12px] text-muted">
                    {t("suppliers.contracts_projects", { contracts: s.contract_count, projects: s.project_count })}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-faint shrink-0" />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-line">
                <div>
                  <p className="text-[11px] text-faint font-medium">{t("suppliers.invoiced")}</p>
                  <p className="text-[15px] font-bold text-night tabular mt-0.5">{fmt(s.total_invoiced, s.currency)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-faint font-medium">{t("suppliers.contract_value")}</p>
                  <p className="text-[15px] font-bold text-night tabular mt-0.5">{fmt(s.total_contract_value, s.currency)}</p>
                </div>
              </div>

              {pct !== null && (
                <div className="mt-3.5">
                  <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <span className="text-faint font-medium">{t("suppliers.consumption")}</span>
                    <span className={`font-semibold tabular ${over ? "text-neg" : "text-night"}`}>{pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
                    <div className={`h-full rounded-full ${over ? "bg-neg" : "bg-night"}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 mt-4 text-[12px]">
                {s.flagged > 0 && (
                  <span className="inline-flex items-center gap-1 text-neg font-semibold">
                    <AlertTriangle className="w-3.5 h-3.5" /> {t("suppliers.flagged", { count: s.flagged })}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-muted">
                  <Mail className="w-3.5 h-3.5" /> {t("suppliers.approved_emails", { count: s.approved_emails.length })}
                </span>
              </div>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
